import TableComponent from "../../components/organism/Table/TableComponent";
import { Dispatch, FC, SetStateAction, useMemo, useState } from "react";
import { Box, Center } from "@chakra-ui/react";
import { DownloadIcon, EditIcon } from "@chakra-ui/icons";
import { CellProps, Column } from "react-table";
import { primaryColor } from "../../utils/colors";
import { handleFileDownload } from "../../helpers/handleFileDownload";
import { Toast } from "../../components/atom/Toast/Toast";
import { getDocumentDownload } from "../../services/Documents/getDocumentDownload";
import { DocumentType } from "../../types/Documents/DocumentType";
import { AuthUser } from "../../types/User/AuthUser";
import SpinnerComponent from "../../components/atom/Spinner/SpinnerComponent";

interface TableDocumentsProps {
  dataTable: any[];
  isLoaded: boolean;
  setIdUpdateItem: (id: number) => void;
  openModal: (newTitle: string, newButtonTitle: string) => void;
  setValue: (key: string, value: any) => void;
  setUploadedFile: Dispatch<SetStateAction<File | null | undefined>>;
  setLoadingFile: Dispatch<SetStateAction<boolean>>;
  user: AuthUser;
}

const TableDocuments: FC<TableDocumentsProps> = ({
  dataTable,
  isLoaded,
  setIdUpdateItem,
  openModal,
  setValue,
  setUploadedFile,
  setLoadingFile,
  user,
}) => {
  const { addToast } = Toast();
  const [loadingTableFile, setLoadingTableFile] = useState<{
    [key: number]: boolean;
  }>({});

  const handleDownload = (document: DocumentType) => {
    setLoadingTableFile((prev) => ({ ...prev, [document.id]: true }));
    fetchDocument(document.id).then((result) => {
      if (result) {
        const file = new File([result.data], `${document.name}.pdf`, {
          type: result.data.type,
        });
        handleFileDownload(file);
      }
      setLoadingTableFile((prev) => ({ ...prev, [document.id]: false }));
    });
  };

  const fetchDocument = (documentId: string | number) => {
    setLoadingFile(true);
    return getDocumentDownload(documentId)
      .then((result) => {
        if (result) {
          setLoadingFile(false);
          return result;
        } else {
          setLoadingFile(false);
          addToast({
            status: "warning",
            title: "Alerta",
            description: "O arquivo PDF está faltando.",
          });
          return null;
        }
      })
      .catch((error) => {
        setLoadingFile(false);
        console.log("API Error:", error);
        return null;
      });
  };

  const columns = useMemo(() => {
    const baseColumns: Column[] = [
      { Header: "Nome", accessor: "name" },
      {
        Header: "Província",
        accessor: (row: any) =>
          row.provinces && row.provinces.length > 0
            ? row.provinces
                .slice(0, 3)
                .map((prov: any) => prov.name)
                .join(", ") + (row.provinces.length > 3 ? "..." : "")
            : "-",
        disableSortBy: true,
      },
      { Header: "Autor", accessor: "authorName", disableSortBy: true },
      {
        Header: "Área específica",
        accessor: (row: any) =>
          row.subSystems && row.subSystems.length > 0
            ? row.subSystems
                .slice(0, 3)
                .map((sub: any) => sub.name)
                .join(", ") + (row.subSystems.length > 3 ? "..." : "")
            : "-",
        disableSortBy: true,
      },
      {
        Header: "Ano publicação",
        accessor: "publishedAt",
        disableSortBy: true,
      },
      {
        Header: "Descarregar",
        accessor: "download",
        disableSortBy: true,
        Cell: ({ row }: CellProps<any, string>) => (
          <Center justifyContent="flex-end">
            {!loadingTableFile[row.original.id] ? (
              <DownloadIcon
                aria-label="Download"
                mr="8"
                color={primaryColor}
                boxSize={6}
                onClick={() => handleDownload(row.original)}
              />
            ) : (
              <Box mr="8" marginY="-6px">
                <SpinnerComponent size="md" />
              </Box>
            )}
          </Center>
        ),
      },
    ];

    if (user?.permissions && user.permissions.includes("DeleteDocument")) {
      baseColumns.push({
        Header: "Gerir",
        accessor: "edit",
        disableSortBy: true,
        Cell: ({ row }: CellProps<any, string>) => (
          <Center justifyContent="flex-end">
            <EditIcon
              color={primaryColor}
              boxSize={6}
              mr="10px"
              cursor="pointer"
              onClick={() => {
                openModal("Gerir documento", "Guardar");
                setIdUpdateItem(row.original.id);
                setValue("name", row.original.name);
                setValue("authorName", row.original.authorName);
                setValue("provinceOptions", row.original.provinces);
                setValue("subSystemOptions", row.original.subSystems);
                setValue("publishedAt", row.original.publishedAt);
                setValue(
                  "permission",
                  row.original.isPrivate
                    ? { id: 1, name: "Privado" }
                    : { id: 2, name: "Público" },
                );
                fetchDocument(row.original.id).then((result) => {
                  if (result) {
                    const file = new File(
                      [result.data],
                      `${row.original.name}.pdf`,
                      {
                        type: result.data.type,
                      },
                    );
                    setUploadedFile(file);
                  }
                });
              }}
            />
          </Center>
        ),
      });
    }
    return baseColumns;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataTable, openModal]);

  return (
    <TableComponent
      isLoaded={isLoaded}
      columns={columns}
      data={dataTable}
      fixedColumnIds={[
        { value: "download", hasShadow: true },
        { value: "edit" },
      ]}
    />
  );
};

export default TableDocuments;
